import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import {
  connectionsApi,
  providerAppsApi,
  type ConnectionView,
  type ProviderAppView,
} from '@/lib/connections';
import {
  Button,
  Card,
  Eyebrow,
  Notice,
  PageHeading,
  SkeletonRows,
  StatusChip,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConsentExplainer } from './ConsentExplainer';
import { ProviderAppSetup } from './ProviderAppSetup';
import { ZenCoreKeyCard } from './ZenCoreKeyCard';

/**
 * Screen 4 — Connections. One card per provider.
 *
 * Connected shows the domain, who consented, rooms discovered, the permissions
 * actually granted, and re-check / disconnect. Not connected shows what we will
 * read, in plain words, before they click anything.
 *
 * The states worth looking at closely are the ones between: `verifying`, and
 * read-without-write. Neither is "connected" and neither is "broken", and the
 * whole point of this screen is that it says so in words.
 */
export function Connections() {
  const [connections, setConnections] = useState<ConnectionView[] | null>(null);
  const [apps, setApps] = useState<ProviderAppView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [explaining, setExplaining] = useState<ConnectionView | null>(null);
  /** The connection whose disconnect is being confirmed. */
  const [disconnecting, setDisconnecting] = useState<ConnectionView | null>(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      // Both together: the card cannot be drawn without knowing whether this
      // organisation has registered an application, and the setup form cannot
      // be drawn without the redirect URI to offer.
      const [connectionList, appList] = await Promise.all([
        connectionsApi.list(),
        providerAppsApi.list(),
      ]);
      setConnections(connectionList);
      setApps(appList);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not load your connections.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Verification runs after the redirect and takes a few seconds, so while any
  // connection says `verifying` the screen re-reads itself rather than leaving
  // someone looking at a spinner that never resolves.
  useEffect(() => {
    if (!connections?.some((c) => c.state === 'verifying')) return;
    const timer = setTimeout(() => void load(), 3000);
    return () => clearTimeout(timer);
  }, [connections, load]);

  // Drives where the API-key card sits, not whether it renders — the card
  // decides that for itself.
  const keyStored = Boolean(connections?.some((c) => c.zencore_key_configured));

  const connectError = params.get('connect_error');
  const justConnected = params.get('connected');

  async function beginConsent(connection: ConnectionView) {
    setBusy(connection.provider);
    try {
      const { consent_url } = await connectionsApi.beginConsent(connection.provider);
      // A full navigation, not a popup: the consent screen is the provider's
      // and a blocked popup is a support ticket.
      window.location.href = consent_url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not start the connection.');
      setBusy(null);
    }
  }

  async function verify(connection: ConnectionView) {
    if (!connection.connection_id) return;
    setBusy(connection.provider);
    try {
      await connectionsApi.verify(connection.connection_id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not re-check that connection.');
    } finally {
      setBusy(null);
    }
  }

  /** Confirmed in our own dialog — see `ConfirmDialog` for why not the browser's. */
  async function disconnect(connection: ConnectionView) {
    if (!connection.connection_id) return;

    setBusy(connection.provider);
    try {
      await connectionsApi.disconnect(connection.connection_id);
      setDisconnecting(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not disconnect that account.');
      setDisconnecting(null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[900px] px-[28px] py-[40px]">
      <div className="mb-[28px]">
        <PageHeading
          eyebrow="Calendar"
          title="Connections"
          sub="Connect the account your organisation already uses. We only ever read the rooms you tell us about, and we never change anything in your directory."
        />
      </div>

      {justConnected && (
        <div className="mb-[20px]">
          <Notice tone="healthy" title="Access granted">
            We are checking what we can do with your calendar. This takes a few seconds.
          </Notice>
        </div>
      )}

      {connectError && (
        <div className="mb-[20px]">
          <Notice tone="broken" title="That did not complete">
            {connectError}{' '}
            <button
              type="button"
              className="border-0 bg-transparent p-0 underline"
              onClick={() => {
                params.delete('connect_error');
                setParams(params, { replace: true });
              }}
            >
              Dismiss
            </button>
          </Notice>
        </div>
      )}

      {error && (
        <div className="mb-[20px]">
          <Notice tone="broken" title={error} />
        </div>
      )}

      {connections === null ? (
        <SkeletonRows rows={2} label="Loading your connections…" />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-[20px] p-0">
          {/* First while it is missing, last once it is stored.
              It blocks mapping — every mapping half-registers without it — and
              a blocker below two tall provider cards is a blocker nobody
              finds. Once it is in, it is maintenance and belongs at the end. */}
          {!keyStored && (
            <li>
              <ZenCoreKeyCard connections={connections} onSaved={load} />
            </li>
          )}
          {connections.map((connection) => (
            <li key={connection.provider}>
              <ConnectionCard
                connection={connection}
                app={apps.find((a) => a.provider === connection.provider) ?? null}
                busy={busy === connection.provider}
                onConnect={() => setExplaining(connection)}
                onVerify={() => verify(connection)}
                onDisconnect={() => setDisconnecting(connection)}
                onAppChanged={load}
              />
            </li>
          ))}
          {keyStored && (
            <li>
              <ZenCoreKeyCard connections={connections} onSaved={load} />
            </li>
          )}
        </ul>
      )}

      {/* Over the cards rather than instead of them. Replacing the screen read
          as navigation: the connection cards vanished, so the choice looked
          already made and "Back to connections" was the only clue it was not. */}
      <ConsentExplainer
        open={explaining !== null}
        connection={explaining}
        busy={explaining !== null && busy === explaining.provider}
        onCancel={() => setExplaining(null)}
        onContinue={() => explaining && void beginConsent(explaining)}
      />

      <ConfirmDialog
        open={disconnecting !== null}
        title={`Disconnect ${disconnecting?.display_name ?? ''}?`}
        confirmLabel="Disconnect"
        cancelLabel="Stay connected"
        busy={busy !== null && disconnecting !== null}
        onConfirm={() => disconnecting && void disconnect(disconnecting)}
        onCancel={() => busy === null && setDisconnecting(null)}
      >
        <ul className="m-0 flex list-none flex-col gap-[8px] p-0">
          <li className="flex gap-[8px]">
            <span aria-hidden="true">✓</span>
            Meetings already in your calendar are left exactly as they are. Nothing is deleted.
          </li>
          <li className="flex gap-[8px]">
            <span aria-hidden="true">–</span>
            Your discovered rooms and the pod links for this account are removed here, so you
            will link your pods again after connecting.
          </li>
          <li className="flex gap-[8px]">
            <span aria-hidden="true">↺</span>
            Reconnecting is always possible; we tell {disconnecting?.display_name ?? 'the provider'}{' '}
            to revoke our access on the way out.
          </li>
        </ul>
      </ConfirmDialog>
    </div>
  );
}

const TONE_TO_CHIP: Record<ConnectionView['tone'], 'healthy' | 'attention' | 'broken' | 'neutral'> =
  { healthy: 'healthy', attention: 'attention', broken: 'broken', neutral: 'neutral' };

function ConnectionCard({
  connection,
  app,
  busy,
  onConnect,
  onVerify,
  onDisconnect,
  onAppChanged,
}: {
  connection: ConnectionView;
  app: ProviderAppView | null;
  busy: boolean;
  onConnect: () => void;
  onVerify: () => void;
  onDisconnect: () => void;
  onAppChanged: () => void | Promise<void>;
}) {
  const isConnected = Boolean(connection.connection_id) && connection.state !== 'not_connected';

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-[14px]">
        <div className="min-w-0">
          <Eyebrow>Calendar</Eyebrow>
          <h2 className="mt-[4px] mb-[8px] text-heading font-semibold text-ink">
            {connection.display_name}
          </h2>
          {/* Never colour alone: a chip carries a glyph and a word. */}
          <StatusChip tone={TONE_TO_CHIP[connection.tone]}>{connection.headline}</StatusChip>
        </div>

        <div className="flex shrink-0 flex-wrap gap-[8px]">
          {connection.state === 'not_configured' ? null : isConnected ? (
            <>
              <Button tone="quiet" busy={busy} onClick={onVerify}>
                Re-check
              </Button>
              <Button tone="danger" disabled={busy} onClick={onDisconnect}>
                Disconnect
              </Button>
            </>
          ) : (
            <Button tone="primary" busy={busy} onClick={onConnect}>
              Connect {connection.display_name}
            </Button>
          )}
        </div>
      </div>

      {connection.next_step && (
        <p className="mt-[14px] mb-0 max-w-[70ch] text-body text-muted">
          {connection.next_step}
        </p>
      )}

      {isConnected && (
        <dl className="mt-[20px] grid grid-cols-1 gap-x-[28px] gap-y-[14px] border-t border-divider pt-[20px] sm:grid-cols-2">
          <Fact label="Domain" value={connection.domain ?? 'Not reported'} />
          <Fact label="Connected by" value={connection.consented_by ?? 'Unknown'} />
          <Fact
            label="Rooms discovered"
            value={
              connection.last_discovery_at
                ? String(connection.rooms_discovered)
                : 'Not looked yet'
            }
          />
          <Fact
            label="Last checked"
            value={
              connection.last_verified_at ? (
                <span className="tabular-nums">
                  {new Date(connection.last_verified_at).toLocaleString()}
                </span>
              ) : (
                'Never'
              )
            }
          />
        </dl>
      )}

      {isConnected && connection.granted_scopes.length > 0 && (
        <details className="mt-[14px]">
          <summary className="cursor-pointer text-body text-brand">
            What you granted us
          </summary>
          <ul className="mt-[8px] mb-0 flex list-none flex-col gap-[8px] p-0">
            {connection.requested_access
              .filter((access) => connection.granted_scopes.includes(access.scope))
              .map((access) => (
                <li key={access.scope} className="text-body text-muted">
                  {access.reason}
                </li>
              ))}
          </ul>
        </details>
      )}

      {/* The application comes first: without one there is nothing to connect
          to. Once registered it collapses to one line, because an administrator
          coming back to this card is usually here for something else. */}
      <ProviderAppSetup connection={connection} app={app} onSaved={onAppChanged} />

      {!isConnected && connection.state !== 'not_configured' && (
        <div className="mt-[20px] border-t border-divider pt-[20px]">
          <p className="zs-eyebrow m-0">What we will ask for</p>
          <p className="mt-[8px] mb-[8px] text-body text-muted">
            When you connect, we will ask your {connection.display_name} administrator for:
          </p>
          <ul className="m-0 flex list-none flex-col gap-[8px] p-0">
            {connection.requested_access.slice(0, 2).map((access) => (
              <li key={access.scope} className="text-body text-ink">
                {access.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="zs-eyebrow m-0">{label}</dt>
      <dd className="m-0 mt-[4px] text-lead text-ink">{value}</dd>
    </div>
  );
}
